import { FC } from 'react';
import { format0 } from 'utils/number';
import type { AddressGroup, AddressVariation, Run } from '../types';
import { ReverseConfirmModal } from '../shared';

// The three reversal confirmations for an address run — a whole execution, a
// group, or a single address — each open off its own pending selection.
export const AddressReverseModals: FC<{
  runToReverse: Run | null;
  groupToReverse: AddressGroup | null;
  variationToReverse: AddressVariation | null;
  onCloseRun: () => void;
  onConfirmRun: () => void;
  onCloseGroup: () => void;
  onConfirmGroup: () => void;
  onCloseVariation: () => void;
  onConfirmVariation: () => void;
}> = ({
  runToReverse,
  groupToReverse,
  variationToReverse,
  onCloseRun,
  onConfirmRun,
  onCloseGroup,
  onConfirmGroup,
  onCloseVariation,
  onConfirmVariation,
}) => {
  const partialToReverse = runToReverse?.partiallyReversed ?? 0;
  return (
    <>
      <ReverseConfirmModal
        open={!!runToReverse}
        heading="Reverse execution"
        confirmText="Reverse execution"
        subject="execution"
        onClose={onCloseRun}
        onConfirm={onConfirmRun}
      >
        This reverses the {format0(runToReverse?.combined ?? 0)} combined group
        {(runToReverse?.combined ?? 0) === 1 ? '' : 's'}
        {partialToReverse > 0 &&
          ` and ${format0(partialToReverse)} partially reversed group${
            partialToReverse === 1 ? '' : 's'
          }`}{' '}
        from execution #{runToReverse?.id}: discarded addresses are restored and
        the document citations they gave their keepers are returned.
      </ReverseConfirmModal>

      <ReverseConfirmModal
        open={!!groupToReverse}
        heading="Reverse address group"
        confirmText="Reverse group"
        subject="group"
        onClose={onCloseGroup}
        onConfirm={onConfirmGroup}
      >
        This restores the discarded addresses of{' '}
        <strong>{groupToReverse?.normalizedName}</strong> and returns the
        document citations they gave the keeper. Other groups in the execution
        are untouched.
      </ReverseConfirmModal>

      <ReverseConfirmModal
        open={!!variationToReverse}
        heading="Reverse address"
        confirmText="Reverse address"
        subject="address"
        onClose={onCloseVariation}
        onConfirm={onConfirmVariation}
      >
        This restores <strong>{variationToReverse?.text}</strong> and returns
        the document citations it gave the keeper. The group&apos;s other
        addresses stay combined.
      </ReverseConfirmModal>
    </>
  );
};
